/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { userSessionsMint } from "../funcs/userSessionsMint.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { MintUserSessionResponseBody } from "../models/components/mintusersessionresponsebody.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  MintUserSessionRequest,
  MintUserSessionSecurity,
} from "../models/operations/mintusersession.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type MintUserSessionMutationVariables = {
  request: MintUserSessionRequest;
  security?: MintUserSessionSecurity | undefined;
  options?: RequestOptions;
};

export type MintUserSessionMutationData = MintUserSessionResponseBody;

export type MintUserSessionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * mintUserSession userSessions
 *
 * @remarks
 * Mint a user_session on behalf of the authenticated dashboard user, bound to an issuer-gated audience: an MCP server, a meta MCP server, or a legacy toolset without an mcp_servers wrapper. Exactly one of toolset_id, mcp_server_id, or meta_mcp_server_id must be provided. The minted JWT matches the shape /token would emit after a successful OAuth dance, so the runtime MCP gateway validates it through the same path as a real MCP client's bearer.
 */
export function useMintUserSessionMutation(
  options?: MutationHookOptions<
    MintUserSessionMutationData,
    MintUserSessionMutationError,
    MintUserSessionMutationVariables
  >,
): UseMutationResult<
  MintUserSessionMutationData,
  MintUserSessionMutationError,
  MintUserSessionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildMintUserSessionMutation(client, options),
    ...options,
  });
}

export function mutationKeyMintUserSession(): MutationKey {
  return ["@gram/client", "userSessions", "mint"];
}

export function buildMintUserSessionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: MintUserSessionMutationVariables,
  ) => Promise<MintUserSessionMutationData>;
} {
  return {
    mutationKey: mutationKeyMintUserSession(),
    mutationFn: function mintUserSessionMutationFn({
      request,
      security,
      options,
    }): Promise<MintUserSessionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(userSessionsMint(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
